import { getMutableComponentProps } from "./utils";
import { DatapointIdStandardEnum } from "api/ux/src";
import { TopFilterGroup } from "components/config/grid-templates/predefined-filters";
import { DatapointGroupingType } from "datapoints";
import { datapointStart } from "datapoints/constants";
const isDatapointOfEntity = (dp, entity) => dp?.standardDatapoint?.toLocaleLowerCase().startsWith(entity) ||
    dp?.customDatapoint?.toLocaleLowerCase().startsWith(entity);
const moveDatapoint = (datapoint, fromArray, toArray) => {
    fromArray.splice(fromArray.indexOf(datapoint), 1);
    toArray.push(datapoint);
};
const isExcludedFromDimensions = (d, forceClientSideGroupings) => {
    const excludedDimensions = forceClientSideGroupings?.excludedDimensions;
    return excludedDimensions?.includes(d.standardDatapoint ?? "") ?? false;
};
const shouldForceClientSideGroupings = (argument) => {
    let forceClientSideGroupings = argument?.forceClientSideGroupings;
    if (forceClientSideGroupings) {
        forceClientSideGroupings.excludedDimensions = [...forceClientSideGroupings?.excludedDimensions || [], "Fund.InvestorName"];
    }
    else {
        forceClientSideGroupings = { excludedDimensions: ["Fund.InvestorName"] };
    }
    return forceClientSideGroupings;
};
const processDatapointMeasure = (datapointMeasure, transformedMeasures, transformedDimensions, hasInitialFundDimensions) => {
    const conditions = [
        { action: () => moveDatapoint(datapointMeasure, transformedMeasures, transformedDimensions.fund),
            condition: isDatapointOfEntity(datapointMeasure, datapointStart.fundsDatapointStart) },
        { action: () => moveDatapoint(datapointMeasure, transformedMeasures, transformedDimensions.asset),
            condition: isDatapointOfEntity(datapointMeasure, datapointStart.assetsDatapointStart) && !hasInitialFundDimensions },
        { action: () => moveDatapoint(datapointMeasure, transformedMeasures, transformedDimensions.manager),
            condition: isDatapointOfEntity(datapointMeasure, datapointStart.managerDatapointStart) }
    ];
    for (const { condition, action } of conditions) {
        if (condition) {
            action();
            break;
        }
    }
};
export const moveAssetIdDimensionToTop = (dpp, userSelectedGroups, moveAssetIdDimensionOnTop) => {
    if (!moveAssetIdDimensionOnTop || !dpp.dimensions || !userSelectedGroups.length) {
        return;
    }
    const isAssetIdDimension = (d) => d?.standardDatapoint === DatapointIdStandardEnum.AssetAssetId;
    const hasAssetIdDimension = dpp.dimensions.some(isAssetIdDimension);
    const isGroupAssetOrCompany = ["AssetId", "CompanyId"].includes(userSelectedGroups[0]);
    if (!hasAssetIdDimension || !isGroupAssetOrCompany) {
        return;
    }
    const assetIdDimension = dpp.dimensions.find(isAssetIdDimension);
    if (!assetIdDimension) {
        return;
    }
    const index = dpp.dimensions.indexOf(assetIdDimension);
    if (index !== -1) {
        dpp.dimensions.splice(index, 1);
        dpp.dimensions.unshift(assetIdDimension);
    }
};
const assignMeasures = (dpp, transformedMeasures, newDimensions, keepOriginalDimensionsOrder) => {
    if (keepOriginalDimensionsOrder) {
        dpp.measures = transformedMeasures?.filter((x) => !newDimensions.some((y) => y.name === x.name));
    }
    else {
        dpp.measures = transformedMeasures;
    }
};
const assignDimensions = ({ dpp, newDimensions, keepOriginalDimensionsOrder, fund, asset, manager }) => {
    if (keepOriginalDimensionsOrder) {
        // Keep the order from newDimensions (which follows userSelectedGroups order)
        dpp.dimensions = newDimensions.concat(dpp?.dimensions || []);
    }
    else {
        // Keep user selection order by using newDimensions directly, then add remaining originals
        const allOriginals = []
            .concat(manager.original)
            .concat(asset.original)
            .concat(fund.original);
        dpp.dimensions = newDimensions.concat(allOriginals);
    }
};
const isUserSelectedGrouping = (usg) => usg.length === 0 || (usg.length === 1 && usg[0] === TopFilterGroup.customGroup);
const getKeepOriginalDimensionsOrder = (argument) => argument?.keepOriginalDimensionsOrder || false;
const getMoveAssetIdDimensionOnTop = (argument) => argument?.moveAssetIdDimensionOnTop || false;
const hasGroupInDimensions = (dpp, group) => dpp?.dimensions?.some((d) => d.name === group);
const getMatchingDatapointMeasure = (dpp, group, forceClientSide) => dpp?.measures?.find((d) => d.name === group && !isExcludedFromDimensions(d, forceClientSide));
const handleUserSelectedGroup = (group, dpp, forceClientSide, newDimensions, transformedMeasures, transformeDimensions, hasInitialFundDimensions) => {
    if (hasGroupInDimensions(dpp, group)) {
        return;
    }
    const datapointMeasure = getMatchingDatapointMeasure(dpp, group, forceClientSide);
    if (!datapointMeasure) {
        return;
    }
    newDimensions.push(datapointMeasure);
    processDatapointMeasure(datapointMeasure, transformedMeasures, transformeDimensions, hasInitialFundDimensions);
};
export const moveSelectedGroupByColumns = (componentProps, allDependencies, originalComponentProps, argument) => {
    const { switched, userSelectedGroups } = allDependencies;
    const keepOriginalDimensionsOrder = getKeepOriginalDimensionsOrder(argument);
    const moveAssetIdDimensionOnTop = getMoveAssetIdDimensionOnTop(argument);
    const forceClientSideGroupings = shouldForceClientSideGroupings(argument);
    if (isUserSelectedGrouping(userSelectedGroups)) {
        return componentProps;
    }
    const mutableComponentProps = getMutableComponentProps(componentProps, originalComponentProps);
    const gridSwitcher = mutableComponentProps?.gridSwitcher, params = switched && gridSwitcher ? gridSwitcher.datapointParams : mutableComponentProps.datapointParams;
    params?.forEach((dpp) => {
        const newDimensions = [];
        const transformedDimensions = dpp?.dimensions || [], transformedMeasures = dpp?.measures || [];
        const assetDimensions = transformedDimensions.filter((d) => isDatapointOfEntity(d, datapointStart.assetsDatapointStart)), fundDimensions = transformedDimensions.filter((d) => isDatapointOfEntity(d, datapointStart.fundsDatapointStart)), hasInitialFundDimensions = fundDimensions && fundDimensions.length > 0, managerDimensions = transformedDimensions.filter((d) => isDatapointOfEntity(d, datapointStart.managerDatapointStart)), transformedAssetDimensions = [], transformedFundDimensions = [], transformedManagerDimensions = [];
        userSelectedGroups.forEach((group) => {
            handleUserSelectedGroup(group, dpp, forceClientSideGroupings, newDimensions, transformedMeasures, {
                asset: transformedAssetDimensions,
                fund: transformedFundDimensions,
                manager: transformedManagerDimensions
            }, hasInitialFundDimensions);
        });
        moveAssetIdDimensionToTop(dpp, userSelectedGroups, Boolean(moveAssetIdDimensionOnTop));
        assignMeasures(dpp, transformedMeasures, newDimensions, keepOriginalDimensionsOrder);
        assignDimensions({
            asset: { original: assetDimensions, transformed: transformedAssetDimensions },
            dpp,
            fund: { original: fundDimensions, transformed: transformedFundDimensions },
            keepOriginalDimensionsOrder,
            manager: { original: managerDimensions, transformed: transformedManagerDimensions },
            newDimensions
        });
    });
    return mutableComponentProps;
};
const removeDimensionsIncompatibleWithGroupingDimensions = (transformedDimensions, transformedMeasures) => {
    const portfolioDatapoint = transformedDimensions.find((d) => d.standardDatapoint === DatapointIdStandardEnum.PortfolioPortfolioId);
    if (portfolioDatapoint) {
        transformedDimensions.splice(transformedDimensions.indexOf(portfolioDatapoint), 1);
    }
    if (!transformedMeasures.some((m) => m.standardDatapoint === DatapointIdStandardEnum.ManagerManagerName)) {
        const managerDatapoint = transformedDimensions.find((d) => d.standardDatapoint === DatapointIdStandardEnum.ManagerManagerId);
        if (managerDatapoint) {
            transformedDimensions.splice(transformedDimensions.indexOf(managerDatapoint), 1);
        }
    }
};
const addDimensionAccordingToSelectedMeasure = (datapointName, groupingDatapoints, dimensions, measures) => {
    const datapointDimension = groupingDatapoints.find((d) => d.name === datapointName);
    if (!datapointDimension) {
        return;
    }
    switch (datapointDimension.type) {
        case DatapointGroupingType.MeasureOnly:
            if (!measures.some((m) => m.name === datapointDimension.name)) {
                measures.push({ periods: 1, ...datapointDimension });
            }
            break;
        case DatapointGroupingType.Dimension:
        default:
            if (!dimensions.some((d) => d.name === datapointDimension.name)) {
                dimensions.push({ periods: 1, ...datapointDimension });
            }
            break;
    }
};
export const addSelectedGroupByColumns = (componentProps, allDependencies, originalComponentProps) => {
    const { switched, userSelectedGroups } = allDependencies;
    if (userSelectedGroups.length === 0) {
        return componentProps;
    }
    const mutableComponentProps = getMutableComponentProps(componentProps, originalComponentProps);
    const gridSwitcher = mutableComponentProps?.gridSwitcher;
    const { datapointParams, groupingDatapoints } = switched && gridSwitcher ? gridSwitcher : mutableComponentProps;
    if (!groupingDatapoints) {
        return mutableComponentProps;
    }
    datapointParams?.forEach((dpp) => {
        const transformedDimensions = dpp?.dimensions || [], transformedMeasures = dpp?.measures || [];
        userSelectedGroups.forEach((group) => {
            addDimensionAccordingToSelectedMeasure(group, groupingDatapoints, transformedDimensions, transformedMeasures);
        });
        removeDimensionsIncompatibleWithGroupingDimensions(transformedDimensions, transformedMeasures);
        dpp.measures = transformedMeasures;
        dpp.dimensions = transformedDimensions;
    });
    return mutableComponentProps;
};
export const replaceDimensionsWithSelectedGroupByColumns = (componentProps, allDependencies, originalComponentProps) => {
    const { switched, userSelectedGroups } = allDependencies;
    const mutableComponentProps = getMutableComponentProps(componentProps, originalComponentProps);
    const gridSwitcher = mutableComponentProps?.gridSwitcher;
    const { datapointParams } = switched && gridSwitcher ? gridSwitcher : mutableComponentProps;
    if (!datapointParams || !userSelectedGroups?.length) {
        return mutableComponentProps;
    }
    let allNewDimensions = [];
    datapointParams?.forEach((dpp) => {
        const newDimensions = [];
        if (userSelectedGroups?.length) {
            const dimensions = (dpp.dimensions || []).toMap((d) => d.name ?? "", (d) => d);
            userSelectedGroups.map((g) => dimensions.get(g)).forEach((g) => g && newDimensions.push(g));
            dpp.dimensions = newDimensions;
            allNewDimensions = allNewDimensions.concat(newDimensions);
        }
    });
    const newColumns = [];
    const selectedGroupColumns = {};
    mutableComponentProps.columnsTemplate.columns.forEach((c) => {
        const col = c;
        const isGroupRow = col && col.field && col.enableRowGroup;
        const isSelectedGroupRow = col && allNewDimensions.some((nd) => nd.name === col.field);
        if (isGroupRow) {
            col.hide = allNewDimensions.length > 0 && !isSelectedGroupRow;
            col.rowGroup = isSelectedGroupRow;
        }
        if (isSelectedGroupRow) {
            selectedGroupColumns[col.field ?? ""] = col;
        }
        else {
            newColumns.push(c);
        }
    });
    mutableComponentProps.columnsTemplate.columns =
        allNewDimensions.map((g) => selectedGroupColumns?.[g.name ?? ""])
            .filter((g) => g)
            .concat(newColumns);
    return mutableComponentProps;
};
